import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { CreateProductVariantData, ProductVariant } from '@/types/api'
import { Pencil, Plus, RefreshCw, Star, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

interface ProductVariantsSectionProps {
  productId: string
  variants: ProductVariant[]
  onChanged: () => Promise<void>
}

type VariantFormState = {
  id?: string
  sku: string
  name: string
  price: number
  stock_quantity: number
  is_active: boolean
  is_default: boolean
  production_status: 'ready' | 'pre_order' | 'discontinued'
  lead_time?: number
}

const getDefaultFormState = (): VariantFormState => ({
  sku: '',
  name: '',
  price: 0,
  stock_quantity: 0,
  is_active: true,
  is_default: false,
  production_status: 'ready',
  lead_time: undefined,
})

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(value)

export function ProductVariantsSection({
  productId,
  variants,
  onChanged,
}: ProductVariantsSectionProps) {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFormVisible, setIsFormVisible] = useState(false)
  const [formState, setFormState] = useState<VariantFormState>(getDefaultFormState())

  const sortedVariants = useMemo(
    () =>
      [...variants].sort(
        (a, b) => Number(b.is_default) - Number(a.is_default) || a.sort_order - b.sort_order
      ),
    [variants]
  )

  const variantSummary = useMemo(() => {
    if (variants.length === 0) {
      return {
        count: 0,
        minPrice: 0,
        maxPrice: 0,
        totalStock: 0,
      }
    }

    const activeVariants = variants.filter((variant) => variant.is_active)
    const source = activeVariants.length > 0 ? activeVariants : variants

    return {
      count: variants.length,
      minPrice: Math.min(...source.map((variant) => variant.price)),
      maxPrice: Math.max(...source.map((variant) => variant.price)),
      totalStock: source.reduce((total, variant) => total + variant.stock_quantity, 0),
    }
  }, [variants])

  const resetForm = () => {
    setFormState(getDefaultFormState())
    setIsFormVisible(false)
  }

  const handleEdit = (variant: ProductVariant) => {
    setFormState({
      id: variant.id,
      sku: variant.sku,
      name: variant.name,
      price: variant.price,
      stock_quantity: variant.stock_quantity,
      is_active: variant.is_active,
      is_default: variant.is_default,
      production_status: variant.production_status ?? 'ready',
      lead_time: variant.lead_time,
    })
    setIsFormVisible(true)
  }

  const buildPayload = (): CreateProductVariantData => ({
    sku: formState.sku.trim(),
    name: formState.name.trim(),
    price: Number(formState.price),
    stock_quantity: Number(formState.stock_quantity),
    is_active: formState.is_active,
    is_default: formState.is_default,
    production_status: formState.production_status,
    lead_time: formState.lead_time,
  })

  const validate = () => {
    if (!formState.sku.trim()) return 'SKU variant wajib diisi.'
    if (!formState.name.trim()) return 'Nama variant wajib diisi.'
    if (Number.isNaN(Number(formState.price)) || Number(formState.price) < 0) {
      return 'Harga variant harus bernilai 0 atau lebih.'
    }
    if (Number.isNaN(Number(formState.stock_quantity)) || Number(formState.stock_quantity) < 0) {
      return 'Stock variant harus bernilai 0 atau lebih.'
    }

    return null
  }

  const handleSubmit = async () => {
    const error = validate()
    if (error) {
      toast({
        title: 'Validation error',
        description: error,
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)

    try {
      if (formState.id) {
        await apiClient.updateProductVariant(productId, formState.id, buildPayload())
      } else {
        await apiClient.createProductVariant(productId, buildPayload())
      }

      await onChanged()
      toast({
        title: 'Success',
        description: formState.id
          ? 'Variant updated successfully.'
          : 'Variant created successfully.',
      })
      resetForm()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save product variant.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (variant: ProductVariant) => {
    if (!window.confirm(`Delete variant ${variant.name}?`)) {
      return
    }

    setIsSubmitting(true)

    try {
      await apiClient.deleteProductVariant(productId, variant.id)
      await onChanged()
      toast({
        title: 'Success',
        description: 'Variant deleted successfully.',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete variant.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleStatus = async (variant: ProductVariant) => {
    setIsSubmitting(true)

    try {
      await apiClient.toggleProductVariantStatus(productId, variant.id, !variant.is_active)
      await onChanged()
      toast({
        title: 'Success',
        description: 'Variant status updated.',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update variant status.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSetDefault = async (variant: ProductVariant) => {
    setIsSubmitting(true)

    try {
      await apiClient.setDefaultProductVariant(productId, variant.id)
      await onChanged()
      toast({
        title: 'Success',
        description: 'Default variant updated.',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to set default variant.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Product Variants</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Saat product punya variants, harga dan stok parent akan mengikuti variant aktif.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{variantSummary.count} variants</Badge>
            <Badge variant="outline">Stock {variantSummary.totalStock}</Badge>
            {variantSummary.count > 0 && (
              <Badge variant="outline">
                {variantSummary.minPrice === variantSummary.maxPrice
                  ? formatCurrency(variantSummary.minPrice)
                  : `${formatCurrency(variantSummary.minPrice)} - ${formatCurrency(variantSummary.maxPrice)}`}
              </Badge>
            )}
            <Button type="button" variant="outline" onClick={onChanged} disabled={isSubmitting}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (isFormVisible && !formState.id) {
                  resetForm()
                  return
                }
                setFormState(getDefaultFormState())
                setIsFormVisible(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {isFormVisible && !formState.id ? 'Cancel' : 'Add Variant'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isFormVisible && (
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <Label htmlFor="variant-name">Variant Name</Label>
                <Input
                  id="variant-name"
                  value={formState.name}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, name: event.target.value }))
                  }
                  placeholder="Contoh: 3 Phase / 2 HP"
                />
              </div>
              <div>
                <Label htmlFor="variant-sku">Variant SKU</Label>
                <Input
                  id="variant-sku"
                  value={formState.sku}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, sku: event.target.value }))
                  }
                  placeholder="SKU-VAR-001"
                />
              </div>
              <div>
                <Label htmlFor="variant-price">Price</Label>
                <Input
                  id="variant-price"
                  type="number"
                  min={0}
                  value={formState.price}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, price: Number(event.target.value || 0) }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="variant-stock">Stock</Label>
                <Input
                  id="variant-stock"
                  type="number"
                  min={0}
                  value={formState.stock_quantity}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      stock_quantity: Number(event.target.value || 0),
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="variant-status">Production Status</Label>
                <select
                  id="variant-status"
                  value={formState.production_status}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      production_status: event.target
                        .value as VariantFormState['production_status'],
                    }))
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="ready">Ready</option>
                  <option value="pre_order">Pre-Order</option>
                  <option value="discontinued">Discontinued</option>
                </select>
              </div>
              <div>
                <Label htmlFor="variant-lead-time">Lead Time (days)</Label>
                <Input
                  id="variant-lead-time"
                  type="number"
                  min={0}
                  value={formState.lead_time ?? ''}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      lead_time: event.target.value ? Number(event.target.value) : undefined,
                    }))
                  }
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  id="variant-active"
                  checked={formState.is_active}
                  onCheckedChange={(checked) =>
                    setFormState((prev) => ({ ...prev, is_active: checked }))
                  }
                />
                <Label htmlFor="variant-active">Active</Label>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  id="variant-default"
                  checked={formState.is_default}
                  onCheckedChange={(checked) =>
                    setFormState((prev) => ({ ...prev, is_default: checked }))
                  }
                />
                <Label htmlFor="variant-default">Default Variant</Label>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button type="button" variant="outline" onClick={resetForm} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : formState.id ? 'Update Variant' : 'Create Variant'}
              </Button>
            </div>
          </div>
        )}

        {sortedVariants.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
            Belum ada variant. Buat variant pertama untuk mulai memisahkan harga dan stok per opsi.
          </div>
        ) : (
          <>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
              Semua image variant sekarang dikelola dari panel <strong>Product Images</strong> di
              kolom kanan, dipisah antara parent product, main variant, dan variant lainnya.
            </div>

            <div className="overflow-x-auto rounded-lg border">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Variant</th>
                    <th className="px-4 py-3 text-left font-medium">SKU</th>
                    <th className="px-4 py-3 text-left font-medium">Price</th>
                    <th className="px-4 py-3 text-left font-medium">Stock</th>
                    <th className="px-4 py-3 text-left font-medium">Images</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Default</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedVariants.map((variant) => (
                    <tr key={variant.id}>
                      <td className="px-4 py-3 font-medium">{variant.name}</td>
                      <td className="px-4 py-3 font-mono text-xs">{variant.sku}</td>
                      <td className="px-4 py-3">{formatCurrency(variant.price)}</td>
                      <td className="px-4 py-3">{variant.stock_quantity}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{variant.images?.length || 0}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={variant.is_active ? 'default' : 'secondary'}>
                          {variant.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {variant.is_default ? (
                          <Badge variant="outline" className="border-amber-300 text-amber-700">
                            <Star className="mr-1 h-3 w-3" />
                            Default
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {!variant.is_default && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => handleSetDefault(variant)}
                              disabled={isSubmitting}
                            >
                              Default
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleStatus(variant)}
                            disabled={isSubmitting}
                          >
                            {variant.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={() => handleEdit(variant)}
                            disabled={isSubmitting}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={() => handleDelete(variant)}
                            disabled={isSubmitting}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
